// eslint-disable-next-line no-unused-vars
import React from 'react';
import { func, number, bool, string } from 'prop-types';
import styled from '@emotion/styled';
import Pagination from 'react-paginate';
import { useTranslation } from 'react-i18next';
import { SkeletonPlaceholder } from '@carbon/react';
import useAnalytics from 'Hooks/useAnalytics';

const StyledToolbar = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-top: 1px solid ${ props => props.theme.products.colors.border };
  padding-top: 15px;
  margin-top: 30px;

  @media screen and (max-width: 480px) {
    justify-content: center;
  }
`;

const StyledSkeleton = styled(SkeletonPlaceholder)`
  width: 100%;
  margin-top: 20px;
  height: 35px;
`;

const StyledInfo = styled.p`
  font-size: 0.875em;
  color: ${ props => props.theme.colors.darkBlue };
  display: flex;
  justify-content: space-evenly;

  span {
    &:first-of-type {
      margin-left: 0;
    }
    font-weight: 600;
    margin: 0 5px;
  }

  @media screen and (max-width: 480px) {
    display: none;
  }
`;

const StyledPagination = styled.div`
  .container {
    display: flex;
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    list-style-type: none;
  }

  li {
    margin: 0 5px;
    border: 1px solid ${ props => props.theme.products.colors.border } !important;
    cursor: pointer !important;

    a {
      display: block;
      height: 100%;
      width: 100%;
      padding: 0;
      line-height: 33px;
      text-align: center;
      font-size: 0.875em;
      color: ${ props => props.theme.colors.darkGray };
      padding: 0 10px;
    }
  }

  li.active {
    background-color: ${ props => props.theme.colors.default };
    pointer-events: none;
    min-width: 0;

    a {
      color: ${ props => props.theme.colors.white };
    }
  }

  li:active {
    outline: none;
    background-color: ${ props => props.theme.colors.default };
    a {
      color: ${ props => props.theme.colors.white };
    }
  }

  .break-me {
    cursor: default;
  }
`;

const NWPagination = ({
  page,
  itemsPerPage,
  itemsCount,
  pageCount,
  onPageChange,
  hrefBuilder,
  loading,
  itemsName = 'items',
}) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();

  if (!itemsCount) {
    return null;
  }

  return loading ? (
    <StyledSkeleton />
  ) : (
    <StyledToolbar>
      <StyledInfo>
        <span>
          { page * itemsPerPage - itemsPerPage + 1 }
          -
          { page === pageCount ? itemsCount : page * itemsPerPage }
        </span>
        { t('common.outOf') }
        <span>{ itemsCount }</span>
        { itemsName.toLowerCase() }
      </StyledInfo>
      <StyledPagination>
        <Pagination
          forcePage={ page - 1 }
          disableInitialCallback
          pageCount={ pageCount }
          pageRangeDisplayed={ 2 }
          marginPagesDisplayed={ 1 }
          previousLabel="<"
          nextLabel=">"
          breakLabel="..."
          breakClassName="break-me"
          containerClassName="container"
          activeClassName="active"
          hrefBuilder={ hrefBuilder }
          onPageChange={ pageData => {
            analytics.track('pagination_page_change', { page_value: pageData.selected + 1 });
            onPageChange(pageData);
          } }
        />
      </StyledPagination>
    </StyledToolbar>
  );
};

NWPagination.propTypes = {
  page: number,
  itemsPerPage: number,
  itemsCount: number,
  pageCount: number,
  hrefBuilder: func,
  onPageChange: func,
  loading: bool,
  itemsName: string,
};

NWPagination.displayName = 'NWPagination';

export { NWPagination };
